import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Lock, Mail } from 'lucide-react';
import { Button, Input, PasswordInput } from '@mlm/ui';
import { login } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useTenantBrand } from '../lib/theme-boot';

/**
 * A field on the dark sign-in backdrop: transparent, 48 px tall, icon on the
 * left. The colours come from the rail tokens, so the login follows the tenant
 * theme exactly as the rail does.
 */
const FIELD =
  'h-12 pl-11 text-base bg-transparent border-rail-foreground/30 text-rail-foreground placeholder:text-rail-foreground/50 ring-offset-rail focus-visible:ring-rail-foreground';
const FIELD_ICON =
  'pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-rail-foreground/60';

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const brand = useTenantBrand();
  const [logoBroken, setLogoBroken] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await login({ email, password, totp: totp || undefined });
      // QoL Slice 5b — manager joins the POS allowlist. Managers sign in to
      // approve above-cap discounts (cashier hands them the device) and can
      // also work the till since they hold the cashier perm bundle.
      if (!res.user.roles.some((r) => ['cashier', 'manager', 'admin', 'super_admin'].includes(r))) {
        setError('Account lacks POS access');
        return;
      }
      setSession(res.accessToken, res.user);
      navigate('/');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (msg === 'totp_required') {
        setNeedsTotp(true);
        setError('TOTP code required');
      } else if (msg === 'invalid_totp') {
        setError('Invalid TOTP code');
      } else {
        setError('Invalid email or password');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-rail text-rail-foreground p-6">
      <div className="w-full max-w-sm">
        <div className="mb-[70px] flex justify-center">
          {brand.logoUrl && !logoBroken ? (
            <img
              src={brand.logoUrl}
              alt={brand.name}
              className="max-h-40 max-w-[315px] object-contain"
              onError={() => setLogoBroken(true)}
            />
          ) : (
            <h1 className="text-center text-3xl font-semibold tracking-tight">{brand.name}</h1>
          )}
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="relative">
            <Mail className={FIELD_ICON} />
            <Input
              id="email"
              type="email"
              aria-label="Email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className={FIELD}
              required
            />
          </div>
          <div className="relative">
            <Lock className={FIELD_ICON} />
            <PasswordInput
              id="password"
              aria-label="Password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className={FIELD}
              wrapperClassName="[&>button]:w-12 [&>button]:text-rail-foreground/60"
              required
            />
          </div>
          {needsTotp && (
            <div className="relative">
              <KeyRound className={FIELD_ICON} />
              <Input
                id="totp"
                type="text"
                inputMode="numeric"
                aria-label="TOTP code"
                placeholder="6-digit code"
                value={totp}
                onChange={(e) => setTotp(e.target.value)}
                pattern="\d{6}"
                className={FIELD}
                required
              />
            </div>
          )}
          {error && (
            <p
              className="rounded-md bg-destructive px-3 py-2 text-sm text-destructive-foreground"
              role="alert"
            >
              {error}
            </p>
          )}
          <Button type="submit" disabled={submitting} className="h-12 w-full text-base">
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </div>
    </div>
  );
}
